import axios from 'axios';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import CartModal from '../components/user/CartModal';
import LoginModal from '../components/user/LoginModal';
import RegisterModal from '../components/user/RegisterModal';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl, getImageUrl } from '../utils/config';
import { formatPrice } from '../utils/currency';

const ProductDetailsPage = () => {
  const { productId } = useParams();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [loadingStates, setLoadingStates] = useState({});
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [cartUpdateTrigger, setCartUpdateTrigger] = useState(0);
  // 1. State for cartCount, showCart, showLogin, showRegister, isLoggedIn
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    fetchProductDetails();
    checkAuthStatus();
  }, [productId]);

  useEffect(() => {
    if (product) {
      // Initialize quantity when product loads
      setQuantity(1);
    }
    // eslint-disable-next-line
  }, [product]);

  useEffect(() => {
    if (store && store.redirect_url && !isAdmin) {
      window.location.href = store.redirect_url;
      return;
    }
  }, [store, isAdmin]);

  const handleCartUpdate = () => {
    // Cart update handled by CartModal
  };

  const checkAuthStatus = () => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  };

  const fetchProductDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(getApiUrl(`/products/${productId}`));
      setProduct(response.data.data);

      // Fetch store details using slug
      const storeResponse = await axios.get(getApiUrl(`/stores/${response.data.data.store.slug}`));
      setStore(storeResponse.data.data);
    } catch (error) {
      console.error('Error fetching product details:', error);
      setError('Failed to load product details');
    } finally {
      setLoading(false);
    }
  };

  const calculateDiscountedPrice = () => {
    const price = parseFloat(product?.price) || 0;
    const discount = parseFloat(product?.discount_percentage) || 0;

    if (discount > 0) {
      return price - (price * discount / 100);
    }
    return price;
  };

  const handleAddToCart = async () => {
    // Set loading state
    setLoadingStates(prev => ({ ...prev, addToCart: true }));
    
    if (!isLoggedIn) {
      // Add to localStorage cart when not logged in
      const localCartKey = `cart_${product.store_id}`;
      const existingCart = JSON.parse(localStorage.getItem(localCartKey) || '[]');

      // Check if product already exists in cart
      const existingItemIndex = existingCart.findIndex(item => item.id === product.id);

      if (existingItemIndex >= 0) {
        // Update quantity if item exists
        existingCart[existingItemIndex].quantity += quantity;
      } else {
        // Add new item to cart
        existingCart.push({
          id: product.id,
          name: product.name,
          price: product.price,
          discount_percentage: product.discount_percentage,
          image: product.image,
          quantity: quantity
        });
      }

      // Save updated cart to localStorage
      localStorage.setItem(localCartKey, JSON.stringify(existingCart));

      // Show success message
      toast.success(`${quantity} product(s) added to cart successfully!`, {
        autoClose: 1000,
      });
      setLoadingStates(prev => ({ ...prev, addToCart: false }));
      return;
    }

    // If logged in, add to backend cart
    try {
      const token = localStorage.getItem('token');
      await axios.post(getApiUrl(`/cart/${product.store_id}/add`), {
        product_id: product.id,
        quantity: quantity
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Show success message
      toast.success(`${quantity} product(s) added to cart successfully!`, {
        autoClose: 1000,
      });
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add product to cart', {
        autoClose: 1000,
      });
    }
    
    // Clear loading state
    setLoadingStates(prev => ({ ...prev, addToCart: false }));
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading product...</p>
        </div>
      </div>
    );
  }

  if (error || !product || !store) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Product not found'}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // If store has redirect_url and user is not admin, stay in loading
  if (store && store.redirect_url && !isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Link to={`/stores/${store.slug}`} className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                {store.icon ? (
                  <img
                    src={getImageUrl(store.icon)}
                    alt={store.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                )}
              </div>
              <h1 className="text-xl font-semibold text-gray-900">{store.name}</h1>
            </Link>
          </div>

          {/* Phone Number */}
          {store.phone && (
            <div className="flex items-center space-x-2 text-gray-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span className="text-sm font-medium">{store.phone}</span>
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Product Details */}
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="md:flex">
          {/* Product Image */}
          <div className="md:w-1/2">
            <div className="aspect-square bg-gray-100 flex items-center justify-center overflow-hidden">
              {product.image ? (
                <img
                  src={getImageUrl(product.image)}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <svg className="w-32 h-32 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              )}
            </div>
          </div>

          {/* Product Info */}
          <div className="md:w-1/2 p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">{product.name}</h1>

            {/* Price */}
            <div className="mb-6">
              {parseFloat(product.discount_percentage) > 0 ? (
                <div className="flex items-center space-x-3">
                  <span className="text-3xl font-bold text-green-600">
                    {formatPrice(calculateDiscountedPrice(), store?.currency)}
                  </span>
                  <span className="text-xl text-gray-500 line-through">
                    {formatPrice(product.price, store?.currency)}
                  </span>
                  <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-sm font-medium">
                    {product.discount_percentage}% OFF
                  </span>
                </div>
              ) : (
                <span className="text-3xl font-bold text-gray-900">
                  {formatPrice(product.price, store?.currency)}
                </span>
              )}
            </div>

            {/* Product Details */}
            {product.details && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
                <p className="text-gray-600 leading-relaxed">{product.details}</p>
              </div>
            )}

            {/* Quantity Controls */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Quantity</h3>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                  className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                  </svg>
                </button>

                <span className="text-2xl font-bold text-gray-900 w-16 text-center">{quantity}</span>

                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Add to Cart Button */}
            <button
              onClick={handleAddToCart}
              disabled={loadingStates.addToCart}
              className="w-full bg-emerald-600 text-white py-4 px-6 rounded-xl hover:bg-emerald-700 transition-colors font-medium text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loadingStates.addToCart ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b border-white mr-3"></div>
                  Adding to Cart...
                </>
              ) : (
                'Add to Cart'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* Bottom Navigation Bar */}
    <nav className="fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t border-gray-200 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-around h-16">
          <Link
            to={store ? `/stores/${store.slug}` : '#'}
            className="flex flex-col items-center space-y-1 text-emerald-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <span className="text-xs font-medium">Home</span>
          </Link>

          <Link
            to={store ? `/stores/${store.slug}/about` : '#'}
            className="flex flex-col items-center space-y-1 text-gray-600 hover:text-gray-900"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-xs font-medium">About</span>
          </Link>

          {/* Cart Button */}
          <button
            onClick={() => setShowCart(true)}
            className="flex flex-col items-center space-y-1 text-gray-600 hover:text-gray-900 relative"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
            </svg>
            <span className="text-xs font-medium">Cart</span>
          </button>

          {/* Profile/Login Button */}
          {isLoggedIn ? (
            <Link
              to={store ? `/stores/${store.slug}/account` : '#'}
              className="flex flex-col items-center space-y-1 text-gray-600 hover:text-gray-900"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="text-xs font-medium">Account</span>
            </Link>
          ) : (
            <button
              onClick={() => setShowLogin(true)}
              className="flex flex-col items-center space-y-1 text-gray-600 hover:text-gray-900"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="text-xs font-medium">Account</span>
            </button>
          )}
        </div>
      </div>
    </nav>

    {/* Modals */}
    {showCart && (
      <CartModal
        storeId={product.store_id}
        onClose={() => setShowCart(false)}
        onCartUpdate={handleCartUpdate}
        store={store}
      />
    )}

    {showLogin && (
      <LoginModal
        onClose={() => setShowLogin(false)}
        onRegisterClick={() => {
          setShowLogin(false);
          setShowRegister(true);
        }}
      />
    )}

    {showRegister && (
      <RegisterModal
        onClose={() => setShowRegister(false)}
        onLoginClick={() => {
          setShowRegister(false);
          setShowLogin(true);
        }}
      />
    )}
    </div>
  );
};

export default ProductDetailsPage; 